import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { clientAuthApi, expertAuthApi } from '../lib/http';
import LandingPage from '../pages/public/LandingPage';

type AuthState = 'loading' | 'client' | 'expert' | 'unauthenticated';

const RootRedirect: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>('loading');

  useEffect(() => {
    const checkAuth = async () => {
      // Get last used role from localStorage as a hint
      const lastRole = localStorage.getItem('authRole') as 'client' | 'expert' | null;

      // Check sessions based on last role preference
      if (lastRole === 'expert') {
        // Try expert first, then client
        try {
          await expertAuthApi.session();
          setAuthState('expert');
          return;
        } catch {
          // Expert session failed, try client
          try {
            await clientAuthApi.session();
            localStorage.setItem('authRole', 'client');
            setAuthState('client');
            return;
          } catch {
            // Both failed
            setAuthState('unauthenticated');
          }
        }
      } else {
        // Try client first (default), then expert
        try {
          await clientAuthApi.session();
          setAuthState('client');
          return;
        } catch {
          // Client session failed, try expert
          try {
            await expertAuthApi.session();
            localStorage.setItem('authRole', 'expert');
            setAuthState('expert');
            return;
          } catch {
            // Both failed
            setAuthState('unauthenticated');
          }
        }
      }
    };

    checkAuth();
  }, []);

  // Loading state - show warm cream background matching landing page
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-warm-base flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" />
      </div>
    );
  }

  // Redirect based on auth state
  if (authState === 'client') {
    return <Navigate to="/u" replace />;
  }

  if (authState === 'expert') {
    return <Navigate to="/app/dashboard" replace />;
  }

  // Not authenticated - show landing page
  return <LandingPage />;
};

export default RootRedirect;
